import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { MatDialog } from '@angular/material/dialog';
import { WellHeaderInfoService } from 'app/modules/well-header-information/services/well-header-info.service';
import { Router } from '@angular/router';
import { ExampleFlatNode, WellsNode } from 'app/modules/well-header-information/model/tree-node.model';
import { OverallViewModalComponent } from 'app/modules/well-header-information/pages/wells/overall-view-modal/overall-view-modal.component';
import { ConfirmationModalDialogComponent } from 'app/core/shared/component/confirmation-modal-dialog/confirmation-modal-dialog.component';
import module from "app/modules/well-header-information/well-header-information.module";

@Component({
  selector: 'app-preventive-maintainance',
  templateUrl: './wells.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: module,
  styleUrls: ['./wells.page.scss']
})
export class WellsPage implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatDrawer;

  public selectedField: any
  public selectedPlatform: any
  public fieldList: any = [] // for filter
  public platformList: any = [] // for filter
  public showFiller = true;
  public wellsNode: WellsNode = {
    name: '',
    level: ''
  }

  public selectedNode: any = this.wellsNode

  // Default appear drawer navigation
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.drawer.toggle();
    }, 0);
  }

  //  TREE NODE CONTROLLER
  private transformer = (node: WellsNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      longName: node.longName,
      nodeLevel: node.level,
      id: node[node.level.toLowerCase() + 'Id'] || node.wellStringId,
      object: node,
      level: level,
    };
  }

  treeControl = new FlatTreeControl<ExampleFlatNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this.transformer, node => node.level, node => node.expandable, node => node.children);

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    public dialog: MatDialog,
    private wellHeaderService: WellHeaderInfoService,
    private router: Router,
  ) { }

  hasChild = (_: number, node: ExampleFlatNode) => node.expandable;

  ngOnInit(): void {
    this.selectedField = 'all'
    this.getWellFilter()

    // Subscribe to the observable
    this.wellHeaderService.wellsTree$.subscribe(data => {
      if (!data) return;
      this.dataSource.data = data;
    });

    this.wellHeaderService.parentsNode$.subscribe(parentIds => {
      setTimeout(() => {
        if (parentIds?.length != 0) {
          parentIds?.forEach((parentId) => {
            let parentsNode = this.treeControl.dataNodes.find(obj => obj.id == parentId)
            this.treeControl.expand(parentsNode);
          })
        }
      }, 300);
    })
  }

  pressTree(node) {
    this.selectedNode = node

    const routeMap = {
      'Well': 'well',
      'Wellbore': 'wellbore',
      'WellString': 'wellString'
    };

    const routeSegment = routeMap[node.nodeLevel];

    if (routeSegment) {
      this.router.navigate([`data-management/well-header-info/wells/${routeSegment}`, node.id]);
      this.treeControl.expand(node);
    }
  }

  getWellFilter() {
    this.wellHeaderService.getWellFilter('').subscribe((res) => {
      this.fieldList = res.data.map(field => field);
      this.platformList = res.data.reduce((acc, field) => acc.concat(field.platforms), []);
      this.platformList.sort((a, b) => a.platformLongName.localeCompare(b.platformLongName));
    })
  }

  onSelectField() {
    if (this.selectedField === 'all') {
      this.getWellFilter();
    } else {
      this.wellHeaderService.getWellFilter(this.selectedField.fieldId).subscribe((res) => {
        this.platformList = res.data.flatMap(field => field.platforms);
        this.platformList.sort((a, b) => a.platformLongName.localeCompare(b.platformLongName));
      });
    }
  }

  onSelectPlatform() {
    this.wellHeaderService.updatedSelectedWellPlatform(this.selectedPlatform)
    this.getWellsTree(this.selectedPlatform.platformId)
  }

  getWellsTree(platformId: any) {
    this.wellHeaderService.getWellsTree(platformId).subscribe()
  }

  overallOverviewModal() {
    const dialogRef = this.dialog.open(OverallViewModalComponent, {
      width: "98%",
      maxWidth: "98%",
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
    });
  }

  deleteNode(node) {
    const dialogRef = this.dialog.open(ConfirmationModalDialogComponent, {
      data: {
        title: 'Deleting Node',
        description: 'Would you like to proceed with deleting this item ?',
        selectedItemName: node.longName
      },
      width: "40%"
    });

    dialogRef.afterClosed().subscribe(isConfirm => {
      if (isConfirm) {
        let foundParentNode = this.findParentsNode(node)

        const deleteMethods = {
          'WellString': (id) => this.wellHeaderService.deleteWellString(id),
          'Wellbore': (id) => this.wellHeaderService.deleteWellbore(id),
          'Well': (id) => this.wellHeaderService.deleteWell(id),
        };

        const deleteObservable = deleteMethods[node.nodeLevel]?.(node.id);

        if (deleteObservable) {
          deleteObservable.subscribe(() => {
            this.getWellsTree(this.selectedPlatform.platformId)

            setTimeout(() => {
              foundParentNode.forEach(parentsNode => {
                let foundDeletedParentsNode = this.treeControl.dataNodes.find(obj => obj.id == parentsNode.id)
                this.treeControl.expand(foundDeletedParentsNode);
              })
            }, 400);
          });
        }
      }
    });
  }

  findParentsNode(node) {
    if (node.level == 0) return

    let parentsNode = []
    let nodeLevel = node.level

    while (nodeLevel > 0) {
      const foundDeletedObject = this.treeControl.dataNodes.find(obj => obj.id === node.id);
      const deletedIndex = this.treeControl.dataNodes.indexOf(foundDeletedObject);
      const remainingDataNode = this.treeControl.dataNodes.slice(0, deletedIndex + 1);
      const foundParentsNode = remainingDataNode.reverse().find(obj => obj.level === nodeLevel - 1);

      parentsNode.push(foundParentsNode)
      nodeLevel--
    }

    return parentsNode
  }

  ngOnDestroy() {
    this.wellHeaderService.resetWellsTree();
  }
}
